import React, {memo} from 'react';
import {keyframes} from '@emotion/react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowForward from '@mui/icons-material/ArrowForward';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {borderRadius, colors, fontSize, fontWeight, gradients, overlay} from '@/styles/tokens';
import {sectionHeadingSx} from '@/styles/sectionHeading';

interface MvpAiAccelerationProps {
    heading: string;
    subheading: string;
    buttonText: string;
    centerTitle: string;
    centerSubtitle: string;
    centerCaption: string;
    steps: string[];
    onCtaClick?: () => void;
}

const sectionSx = {bgcolor: colors.primary.deep} as const;

// Heading uses the canonical section-heading scale (`sectionHeadingSx`), which carries
// the shared step-down on laptop-class screens (≤1499px) — same as every section that
// renders through `SectionHeader`. Only colour and spacing are section-specific.
const headingSx = [
    sectionHeadingSx,
    {
        color: colors.text.inverse,
        mb: 1.5
    }
] as const;

const subheadingSx = {color: colors.text.inverse, lineHeight: 1.9, mb: 4} as const;

// Copy gets 5 of 12 columns so the heading and paragraph have room; the diagram keeps
// the rest. A CSS grid rather than MUI `Grid`, whose negative top margin (its spacing)
// would push the section up into the one above it.
const layoutSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', md: 'minmax(0, 5fr) minmax(0, 7fr)'},
    alignItems: 'center',
    gap: {xs: 4, md: 6}
} as const;

// Below `sm` the orb plus two side columns cannot share a phone's width without
// crushing the labels, so the diagram stacks: orb first, then the steps in one flow.
const diagramRowSx = {
    display: {xs: 'grid', sm: 'flex'},
    // Stacked, the two side columns dissolve (`display: contents`) into this one grid so
    // the ten steps run as a single two-column list instead of two lists of five.
    gridTemplateColumns: {xs: 'repeat(2, minmax(0, 1fr))'},
    alignItems: 'center',
    justifyContent: 'center',
    gap: 3,
    flexWrap: 'nowrap'
} as const;

const listSx = (alignEnd: boolean) =>
    ({
        display: {xs: 'contents', sm: 'flex'},
        flexDirection: 'column',
        gap: {sm: 2, md: 3},
        flex: {sm: 1},
        minWidth: 0,
        // The mirrored right-alignment only makes sense once the column sits to the
        // left of the orb; stacked on a phone every label reads from the left.
        textAlign: {xs: 'left', sm: alignEnd ? 'right' : 'left'}
    } as const);

const stepNumberSx = {color: colors.accent.orange, fontSize: fontSize[12], fontWeight: fontWeight.bold, mb: 0.25} as const;

const stepLabelSx = {color: colors.text.inverse, fontSize: fontSize[14], lineHeight: 1.4} as const;

// Everything below animates `transform` and `opacity` only, so the loop stays on the
// compositor and never asks the main thread for layout while the page is scrolling.

// Sonar: a ring leaves the core and dissolves as it reaches the rim.
const orbPing = keyframes({
    '0%': {transform: 'translate(-50%, -50%) scale(0.35)', opacity: 0},
    '15%': {opacity: 0.55},
    '100%': {transform: 'translate(-50%, -50%) scale(1)', opacity: 0}
});

// Carries the rim dot around the circle.
const orbOrbit = keyframes({
    from: {transform: 'rotate(0deg)'},
    to: {transform: 'rotate(360deg)'}
});

// The core breathes rather than blinks — a long, shallow swell under the wordmark.
const orbBreathe = keyframes({
    '0%, 100%': {transform: 'translate(-50%, -50%) scale(0.92)', opacity: 0.45},
    '50%': {transform: 'translate(-50%, -50%) scale(1.08)', opacity: 0.75}
});

// The two static rings drift in and out very slightly, so the orb is never completely
// still between sonar pulses.
const orbDrift = keyframes({
    '0%, 100%': {transform: 'translate(-50%, -50%) scale(1)'},
    '50%': {transform: 'translate(-50%, -50%) scale(1.035)'}
});

// One gate for the whole set: visitors who ask for reduced motion get the orb exactly as
// it is now, with every loop simply absent rather than disabled in JavaScript.
const motionSafe = (styles: Record<string, unknown>) => ({'@media (prefers-reduced-motion: no-preference)': styles});

// Soft lit centre under the wordmark, widening the gradient's core so "AI" sits on light
// rather than on flat navy.
const orbCoreGlowSx = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '58%',
    height: '58%',
    borderRadius: borderRadius.circle,
    background: `radial-gradient(circle, ${overlay.whiteFaint} 0%, transparent 70%)`,
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
    ...motionSafe({animation: `${orbBreathe} 6s ease-in-out infinite`})
} as const;

// Staggered so a new pulse leaves the core as the previous one reaches the rim.
const orbPingSx = (delaySeconds: number) =>
    ({
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '100%',
        height: '100%',
        borderRadius: borderRadius.circle,
        border: `1px solid ${overlay.whiteBorder}`,
        transform: 'translate(-50%, -50%) scale(0.35)',
        opacity: 0,
        pointerEvents: 'none',
        ...motionSafe({animation: `${orbPing} 5.4s ease-out ${delaySeconds}s infinite`})
    } as const);

// The dot is parked at the top of a full-size ring; rotating the ring walks it round the
// rim, which keeps the maths to a single `rotate` instead of per-frame trigonometry.
const orbOrbitRingSx = {
    position: 'absolute',
    inset: 0,
    borderRadius: borderRadius.circle,
    pointerEvents: 'none',
    ...motionSafe({animation: `${orbOrbit} 22s linear infinite`})
} as const;

const orbOrbitDotSx = {
    position: 'absolute',
    top: -3,
    left: '50%',
    width: 6,
    height: 6,
    ml: '-3px',
    borderRadius: borderRadius.circle,
    bgcolor: colors.accent.orange,
    boxShadow: `0 0 10px ${colors.accent.orange}`
} as const;

const orbSx = {
    position: 'relative',
    flexShrink: 0,
    // Stacked, the orb leads the diagram rather than sitting between the two lists.
    order: {xs: -1, sm: 0},
    gridColumn: {xs: '1 / -1'},
    mx: {xs: 'auto'},
    width: {xs: 220, md: 260},
    height: {xs: 220, md: 260},
    borderRadius: borderRadius.circle,
    background: gradients.aiOrb,
    border: `1px solid ${overlay.whiteLight}`,
    // Decorative only, so phones (below 600px) skip it and go straight to the steps.
    display: {xs: 'none', sm: 'flex'},
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center'
} as const;

const orbRingSx = (size: string, delaySeconds: number) =>
    ({
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: size,
        height: size,
        borderRadius: borderRadius.circle,
        border: `1px solid ${overlay.whiteSubtle}`,
        ...motionSafe({animation: `${orbDrift} 9s ease-in-out ${delaySeconds}s infinite`})
    } as const);

const orbContentSx = {position: 'relative', zIndex: 1} as const;

const orbTitleSx = {
    color: colors.text.inverse,
    fontWeight: fontWeight.bold,
    fontSize: {xs: fontSize[40], md: fontSize[56]},
    lineHeight: 1
} as const;

// The tracking is what decides whether this fits on one line, so it steps down with the
// orb: `nowrap` alone would push the label past the rim on the smaller phone circle.
const orbSubtitleSx = {
    color: overlay.whiteSoft,
    fontSize: fontSize[10],
    fontWeight: fontWeight.medium,
    letterSpacing: {xs: '0.14em', md: '0.24em'},
    textTransform: 'uppercase' as const,
    whiteSpace: 'nowrap',
    mt: 1
} as const;

const orbCaptionSx = {color: overlay.whiteDim, fontSize: fontSize[12], mt: 2.5} as const;

const MvpAiAcceleration = ({
    heading,
    subheading,
    buttonText,
    centerTitle,
    centerSubtitle,
    centerCaption,
    steps,
    onCtaClick
}: MvpAiAccelerationProps) => {
    const half = Math.ceil(steps.length / 2);
    const leftSteps = steps.slice(0, half);
    const rightSteps = steps.slice(half);

    return (
        <Box sx={sectionSx}>
            <MUIContainer>
                <Box sx={layoutSx}>
                    <Box>
                        <Typography variant="h1" component="h2" className="font-headers" sx={headingSx}>
                            {heading}
                        </Typography>
                        <Typography variant="subtitle1" component="p" className="font-regular-text" sx={subheadingSx}>
                            {subheading}
                        </Typography>
                        <Button
                            variant="contained"
                            disableRipple={true}
                            className="animated-button nft-button"
                            onClick={onCtaClick}
                            endIcon={<ArrowForward fontSize="small" />}
                        >
                            {buttonText}
                        </Button>
                    </Box>

                    <Box>
                        <Box sx={diagramRowSx}>
                            <Box sx={listSx(true)}>
                                {leftSteps.map((step, idx) => (
                                    <Box key={step}>
                                        <Typography component="p" className="font-headers" sx={stepNumberSx}>
                                            {String(idx + 1).padStart(2, '0')}
                                        </Typography>
                                        <Typography component="p" className="font-regular-text" sx={stepLabelSx}>
                                            {step}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>

                            <Box sx={orbSx}>
                                <Box sx={orbPingSx(0)} aria-hidden="true" />
                                <Box sx={orbPingSx(2.7)} aria-hidden="true" />
                                <Box sx={orbRingSx('84%', 0)} aria-hidden="true" />
                                <Box sx={orbRingSx('62%', 1.2)} aria-hidden="true" />
                                <Box sx={orbCoreGlowSx} aria-hidden="true" />
                                <Box sx={orbOrbitRingSx} aria-hidden="true">
                                    <Box sx={orbOrbitDotSx} />
                                </Box>
                                <Box sx={orbContentSx}>
                                    <Typography component="p" className="font-headers" sx={orbTitleSx}>
                                        {centerTitle}
                                    </Typography>
                                    <Typography component="p" className="font-headers" sx={orbSubtitleSx}>
                                        {centerSubtitle}
                                    </Typography>
                                    <Typography component="p" className="font-regular-text" sx={orbCaptionSx}>
                                        {centerCaption}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={listSx(false)}>
                                {rightSteps.map((step, idx) => (
                                    <Box key={step}>
                                        <Typography component="p" className="font-headers" sx={stepNumberSx}>
                                            {String(half + idx + 1).padStart(2, '0')}
                                        </Typography>
                                        <Typography component="p" className="font-regular-text" sx={stepLabelSx}>
                                            {step}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(MvpAiAcceleration);
